import { FC, FormEvent, useEffect } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { CloseButton, StyledDialog } from "@/components/AlertDialogs/Dialog";
import Button from "@/components/Button";
import { Input } from "@/components/Forms";
import { Modal } from "@/components/Modal";
import { SidePanelFooter } from "@/components/SidePanel";
import Text from "@/components/Text";
import { api } from "@/utils/api";
import ContentSpinner from "@/components/ContentSpinner";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { scrollBarStyles } from "@/utils/theme";
import FormField from "@/components/Forms/FormField";

type FormDataT = {
  bonus_criterion: {
    min_views: number;
    c1: {
      ctr_from: number;
      ctr_to?: number;
      reward: number;
    }[];
    c2: {
      ctr_from: number;
      ctr_to?: number;
      reward: number;
    }[];
  };
  status: string;
};

const Dialog = styled(StyledDialog)`
  width: 696px;
  padding: 0;
  text-align: left;
`;

const StyledInput = styled(Input)`
  padding-left: 35px;
`;

const DialogHeader = styled.div`
  padding: 22px 24px;
`;

const Header = styled.div`
  background: ${({ theme }) => theme.colors.grayscaleLight};
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
`;
const Title = styled(Text)`
  margin-top: 3px;
`;

const StyledSidePanelFooter = styled(SidePanelFooter)`
  text-align: right;
  button {
    margin-left: 16px;
  }
`;

const ModalContent = styled.div`
  overflow: auto;
  max-height: calc(86vh - 340px);
  padding: 24px;
  padding-bottom: 56px;
  ${scrollBarStyles};
`;

const Badge = styled(Text)`
  background: ${({ theme }) => theme.colors.warning};
  border-radius: 4px;
  width: 30px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SecondaryBadge = styled(Badge)`
  background: ${({ theme }) => theme.colors.research};
`;

const TitleHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const ValuesTitle = styled(Text)`
  margin-bottom: 12px;
`;

const ValuesRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  > div {
    flex: 1;
  }
`;

const Break = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.lightGray};
  margin: 32px 0;
`;

const Label = styled(Text).attrs({
  as: "label",
  $type: "smallHeadline",
  $color: "gray",
})`
  margin-bottom: 8px;
  display: block;
`;

const TwoCol = styled.div`
  width: calc(67.29% - 12px);
`;

const OneCol = styled.div`
  width: calc(32.71% - 12px);
`;

const SecondaryValuesRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const InputHolder = styled.div`
  position: relative;
`;

const InputSign = styled(Text)`
  position: absolute;
  left: 16px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
`;

const SuccessButton = styled.span`
  margin-top: 25px;
  display: block;
`;

const TopRow = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  padding-bottom: 24px;
  margin-bottom: 24px;
`;

interface Props {
  competition: {
    id: number;
    ended_at?: string;
  };
  onClose: () => void;
}

const EndCompetitionModal: FC<Props> = ({ onClose, competition }) => {
  const queryClient = useQueryClient();

  const { id } = useParams();

  const alerts = useAlerts();

  const { data: criterias, isLoading } = useQuery(
    ["thumbnails_criteria"],
    () =>
      api.get<
        never,
        {
          min_views: number;
          c1: { ctr_from: number; ctr_to?: number; reward: number }[];
          c2: { ctr_from: number; ctr_to?: number; reward: number }[];
        }
      >("/v1/gamification/thumbnails/bonus-criterion"),
    {
      refetchOnMount: true,
    }
  );

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {},
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        const { success } = await api.patch<typeof data, { success: boolean }>(
          `/v1/gamification/thumbnails/competitions/${competition.id}`,
          data
        );

        if (success) {
          alerts.success(
            "Competition was successfully ended",
            <SuccessButton>
              <Button
                as="a"
                href="/general/gamification/bonuses/thumb"
                target="_blank"
              >
                Open bonuses
              </Button>
            </SuccessButton>
          );
        } else {
          alerts.error(
            "Could not choose winner automatically, check results manually"
          );
        }

        queryClient.invalidateQueries(["optimisation_tests", { id }], {
          exact: false,
        });

        queryClient.invalidateQueries(["bonuses", { type: "thumbnails" }], {
          exact: false,
        });

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  useEffect(() => {
    reset({
      bonus_criterion: {
        min_views: criterias?.min_views,
        c1: criterias?.c1,
        c2: criterias?.c2,
      },
      status: "finished",
    });
  }, [reset, criterias]);

  return (
    <Modal zIndex={10}>
      <Dialog
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.5 }}
      >
        {isLoading ? (
          <ContentSpinner />
        ) : (
          <form onSubmit={onSubmit} autoComplete="off">
            <DialogHeader>
              <Text $type="button">End Competition</Text>
              <CloseButton type="button" onClick={onClose}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13 1L1 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13 13L1 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </CloseButton>
            </DialogHeader>
            <Header>
              <Text $type="smallHeadline" $color="gray">
                Project name
              </Text>
              <Title $type="caption">
                Extreme makeover with gadgets From Nerd to Popular
              </Title>
            </Header>
            <ModalContent>
              <TopRow>
                <OneCol>
                  <FormField<FormDataT>
                    name="bonus_criterion.min_views"
                    control={control}
                    label="Minimum Views"
                    placeholder="Add minimum views"
                  />
                </OneCol>
              </TopRow>
              <TitleHolder>
                <Badge $type="smallBold" $color="white">
                  C1
                </Badge>
                <Text $type="caption">Competition Values</Text>
              </TitleHolder>
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i}>
                  <ValuesTitle $type="smallHeadline">Value {i + 1}</ValuesTitle>
                  {i < 4 ? (
                    <ValuesRow>
                      <div>
                        <Label>CTR (from)</Label>
                        <InputHolder>
                          <InputSign $type="caption">%</InputSign>
                          <StyledInput
                            {...register(`bonus_criterion.c1.${i}.ctr_from`)}
                            $hasError={
                              !!errors.bonus_criterion?.c1?.[i]?.ctr_from
                            }
                          />
                        </InputHolder>
                        {!!errors.bonus_criterion?.c1?.[i]?.ctr_from && (
                          <ValidationMessage>
                            {errors.bonus_criterion?.c1?.[i]?.ctr_from?.message}
                          </ValidationMessage>
                        )}
                      </div>
                      <div>
                        <Label>CTR (to)</Label>
                        <InputHolder>
                          <InputSign $type="caption">%</InputSign>
                          <StyledInput
                            {...register(`bonus_criterion.c1.${i}.ctr_to`)}
                            $hasError={
                              !!errors.bonus_criterion?.c1?.[i]?.ctr_to
                            }
                          />
                        </InputHolder>
                        {!!errors.bonus_criterion?.c1?.[i]?.ctr_to && (
                          <ValidationMessage>
                            {errors.bonus_criterion?.c1?.[i]?.ctr_to?.message}
                          </ValidationMessage>
                        )}
                      </div>
                      <div>
                        <Label>Bonus</Label>
                        <InputHolder>
                          <InputSign $type="caption">€</InputSign>
                          <StyledInput
                            {...register(`bonus_criterion.c1.${i}.reward`)}
                            $hasError={
                              !!errors.bonus_criterion?.c1?.[i]?.reward
                            }
                          />
                        </InputHolder>
                        {!!errors.bonus_criterion?.c1?.[i]?.reward && (
                          <ValidationMessage>
                            {errors.bonus_criterion?.c1?.[i]?.reward?.message}
                          </ValidationMessage>
                        )}
                      </div>
                    </ValuesRow>
                  ) : (
                    <SecondaryValuesRow>
                      <TwoCol>
                        <Label>CTR (from)</Label>
                        <InputHolder>
                          <InputSign $type="caption">%</InputSign>
                          <StyledInput
                            {...register(`bonus_criterion.c1.${i}.ctr_from`)}
                            $hasError={
                              !!errors.bonus_criterion?.c1?.[i]?.ctr_from
                            }
                          />
                        </InputHolder>
                        {!!errors.bonus_criterion?.c1?.[i]?.ctr_from && (
                          <ValidationMessage>
                            {errors.bonus_criterion?.c1?.[i]?.ctr_from?.message}
                          </ValidationMessage>
                        )}
                      </TwoCol>
                      <OneCol>
                        <Label>Bonus</Label>
                        <InputHolder>
                          <InputSign $type="caption">€</InputSign>
                          <StyledInput
                            {...register(`bonus_criterion.c1.${i}.reward`)}
                            $hasError={
                              !!errors.bonus_criterion?.c1?.[i]?.reward
                            }
                          />
                        </InputHolder>
                        {!!errors.bonus_criterion?.c1?.[i]?.reward && (
                          <ValidationMessage>
                            {errors.bonus_criterion?.c1?.[i]?.reward?.message}
                          </ValidationMessage>
                        )}
                      </OneCol>
                    </SecondaryValuesRow>
                  )}
                </div>
              ))}
              <Break />
              <TitleHolder>
                <SecondaryBadge $type="smallBold" $color="white">
                  C2
                </SecondaryBadge>
                <Text $type="caption">Competition Values</Text>
              </TitleHolder>
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i}>
                  <ValuesTitle $type="smallHeadline">Value {i + 1}</ValuesTitle>
                  {i < 4 ? (
                    <ValuesRow>
                      <div>
                        <Label>CTR (from)</Label>
                        <InputHolder>
                          <InputSign $type="caption">%</InputSign>
                          <StyledInput
                            {...register(`bonus_criterion.c2.${i}.ctr_from`)}
                            $hasError={
                              !!errors.bonus_criterion?.c2?.[i]?.ctr_from
                            }
                          />
                        </InputHolder>
                        {!!errors.bonus_criterion?.c2?.[i]?.ctr_from && (
                          <ValidationMessage>
                            {errors.bonus_criterion?.c2?.[i]?.ctr_from?.message}
                          </ValidationMessage>
                        )}
                      </div>
                      <div>
                        <Label>CTR (to)</Label>
                        <InputHolder>
                          <InputSign $type="caption">%</InputSign>
                          <StyledInput
                            {...register(`bonus_criterion.c2.${i}.ctr_to`)}
                            $hasError={
                              !!errors.bonus_criterion?.c2?.[i]?.ctr_to
                            }
                          />
                        </InputHolder>
                        {!!errors.bonus_criterion?.c2?.[i]?.ctr_to && (
                          <ValidationMessage>
                            {errors.bonus_criterion?.c2?.[i]?.ctr_to?.message}
                          </ValidationMessage>
                        )}
                      </div>
                      <div>
                        <Label>Bonus</Label>
                        <InputHolder>
                          <InputSign $type="caption">€</InputSign>
                          <StyledInput
                            {...register(`bonus_criterion.c2.${i}.reward`)}
                            $hasError={
                              !!errors.bonus_criterion?.c2?.[i]?.reward
                            }
                          />
                        </InputHolder>
                        {!!errors.bonus_criterion?.c2?.[i]?.reward && (
                          <ValidationMessage>
                            {errors.bonus_criterion?.c2?.[i]?.reward?.message}
                          </ValidationMessage>
                        )}
                      </div>
                    </ValuesRow>
                  ) : (
                    <SecondaryValuesRow>
                      <TwoCol>
                        <Label>CTR (from)</Label>
                        <InputHolder>
                          <InputSign $type="caption">%</InputSign>
                          <StyledInput
                            {...register(`bonus_criterion.c2.${i}.ctr_from`)}
                            $hasError={
                              !!errors.bonus_criterion?.c2?.[i]?.ctr_from
                            }
                          />
                        </InputHolder>
                        {!!errors.bonus_criterion?.c2?.[i]?.ctr_from && (
                          <ValidationMessage>
                            {errors.bonus_criterion?.c2?.[i]?.ctr_from?.message}
                          </ValidationMessage>
                        )}
                      </TwoCol>
                      <OneCol>
                        <Label>Bonus</Label>
                        <InputHolder>
                          <InputSign $type="caption">€</InputSign>
                          <StyledInput
                            {...register(`bonus_criterion.c2.${i}.reward`)}
                            $hasError={
                              !!errors.bonus_criterion?.c2?.[i]?.reward
                            }
                          />
                        </InputHolder>
                        {!!errors.bonus_criterion?.c2?.[i]?.reward && (
                          <ValidationMessage>
                            {errors.bonus_criterion?.c2?.[i]?.reward?.message}
                          </ValidationMessage>
                        )}
                      </OneCol>
                    </SecondaryValuesRow>
                  )}
                </div>
              ))}
            </ModalContent>
            <StyledSidePanelFooter>
              <Button type="button" $type="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Confirm
              </Button>
            </StyledSidePanelFooter>
          </form>
        )}
      </Dialog>
    </Modal>
  );
};

export default EndCompetitionModal;
